import { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';

import { updateProviderServicesWithSelectedProperty } from './group-hooks/group-hooks-utils';
import { ShippingServiceDropdownSelectedValue } from './shipping-service-dropdown-selected-value';
import { ShippingServiceSearchableDropdown } from './shipping-service-searchable-dropdown';
import { useEnableShippingServiceSearchableDropdown } from '../../feature-flags/hooks/use-enable-shipping-service-searchable-dropdown';
import { useFulfillmentPlannerContext } from '../../fulfillment-planner/context';
import { useServiceFilters } from '../../fulfillment-planner/context/hooks';
import { useGetInitialShippingServiceGroups } from '../../hooks/use-get-initial-shipping-service-groups';
import { ACCESS_LEVELS, useRequiresRateShopperAccessLevel } from '../../hooks/use-has-rate-shopper-access-level';
import { useIsVendorOr3PL } from '../../hooks/use-is-vendor-or-3pl';
import * as CarrierSharingActions from '../../store/carrier-sharing/carrier-sharing.actions';
import { selectCarrierSharingisLoading, selectCarrierSharingisRequired } from '../../store/carrier-sharing/carrier-sharing.selectors';
import Global from '../../store/global-selectors';
import { useBestWayToShipGroup } from '../shipping-service-selector/group-hooks/use-best-way-to-ship-group';
import { useProviderOrCarrierGroups } from '../shipping-service-selector/group-hooks/use-provider-or-carrier-groups';
import { useServiceCollectionGroup } from '../shipping-service-selector/group-hooks/use-service-collection-group';
import { ShippingServiceSelector } from '../shipping-service-selector/index';

const renderSelectedValue = ({ serviceId, sellerLabelProviderId, serviceCollectionGuid, isTemplate }) => {
  if (!serviceCollectionGuid && !serviceId) {
    return;
  }

  return (
    <ShippingServiceDropdownSelectedValue
      isTemplate={isTemplate}
      sellerLabelProviderId={sellerLabelProviderId}
      serviceCollectionGuid={serviceCollectionGuid}
      serviceId={serviceId}
    />
  );
};

/**
 * @param {object} props
 * @param {string} [props.id]
 * @param {'ComboBox'|'Menu'} [props.dropdownMenuType]
 * @param {string} props.fulfillmentPlanId
 * @param {boolean} props.showBestWayToShip
 * @param {boolean} props.dropdownMenuShouldHaveWidthOfButton
 * @param {boolean} props.hasLabelOverFlow
 * @param {boolean} props.isSearchable
 * @returns {JSX.Element}
 */
export const FulfillmentPlannerContextShippingServiceSelector = ({
  id,
  dropdownMenuType,
  fulfillmentPlanId,
  showBestWayToShip = false,
  dropdownMenuShouldHaveWidthOfButton = false,
  hasLabelOverFlow = false,
  isSearchable = false,
}) => {
  const isSearchableDropdownEnabled = useEnableShippingServiceSearchableDropdown();
  const shouldUseSearchableDropdown = isSearchable && isSearchableDropdownEnabled;

  const isLoading = useSelector(selectCarrierSharingisLoading);
  const merchantServicesRequired = useSelector(selectCarrierSharingisRequired);
  const dispatch = useDispatch();
  const {
    availableSellerShippingServices,
    cswTypeId,
    serviceIdAndSellerLabelProviderId,
    serviceCollectionGuid: selectedServiceCollectionGuid,
    onServiceChangeWithSideEffects,
  } = useFulfillmentPlannerContext();
  const selectedSellerLabelProviderId = serviceIdAndSellerLabelProviderId.sellerLabelProviderId;
  const selectedServiceId = serviceIdAndSellerLabelProviderId.serviceId;
  const serviceCollections = useSelector(Global.selectSellerServiceCollections);
  const selectedServiceCollection = serviceCollections.find(
    (collection) => collection.serviceCollectionGuid === selectedServiceCollectionGuid,
  );
  const isTemplate = selectedServiceCollection?.isTemplate;

  const availableServiceWhitelistCB = useCallback(
    (service) => availableSellerShippingServices.includes(Number.parseInt(service.serviceId, 10)),
    [availableSellerShippingServices],
  );
  const isVendorOr3PL = useIsVendorOr3PL();
  const allServices = useSelector(Global.selectSellerServices);
  const serviceFilters = useServiceFilters({ cswTypeId });
  const fullAvailableServices = useMemo(
    () =>
      allServices.filter((service) =>
        [...serviceFilters, availableServiceWhitelistCB].reduce(
          (isAvailable, serviceFilterCB) => isAvailable && serviceFilterCB(service),
          true,
        ),
      ),
    [allServices, availableServiceWhitelistCB, serviceFilters],
  );

  useEffect(() => {
    if (isVendorOr3PL) {
      dispatch(CarrierSharingActions.getSharedServices(fulfillmentPlanId));
    }
  }, [dispatch, isVendorOr3PL, fulfillmentPlanId]);

  const selectedServiceItem = useMemo(
    () => ({
      serviceId: selectedServiceCollectionGuid ? undefined : selectedServiceId,
      sellerLabelProviderId: selectedServiceCollectionGuid ? undefined : selectedSellerLabelProviderId,
      serviceCollectionGuid: selectedServiceCollectionGuid,
    }),
    [selectedServiceId, selectedSellerLabelProviderId, selectedServiceCollectionGuid],
  );

  const providerOrCarrierGroups = useProviderOrCarrierGroups(fullAvailableServices, selectedServiceItem);
  const bestWayToShipGroup = useBestWayToShipGroup(selectedServiceItem);
  const serviceCollectionGroup = useServiceCollectionGroup(selectedServiceItem, shouldUseSearchableDropdown);
  const showRateShopper = useRequiresRateShopperAccessLevel(ACCESS_LEVELS.FREEMIUM);
  const serviceGroups = useGetInitialShippingServiceGroups(providerOrCarrierGroups, merchantServicesRequired);

  const showRateShopperGroup = showRateShopper && serviceCollectionGroup != null && !merchantServicesRequired;
  if (showRateShopperGroup) {
    serviceGroups.unshift(serviceCollectionGroup);
  }

  const showBestWayToShipGroup = showBestWayToShip && bestWayToShipGroup != null && !merchantServicesRequired;
  if (showBestWayToShipGroup) {
    serviceGroups.unshift(bestWayToShipGroup);
  }

  const selectedOption = useMemo(() => {
    return {
      ...selectedServiceItem,
      isTemplate,
    };
  }, [isTemplate, selectedServiceItem]);

  if (isLoading) {
    return (
      <div>
        <LoadingIndicator />
      </div>
    );
  }

  if (shouldUseSearchableDropdown) {
    return (
      <>
        <ShippingServiceSearchableDropdown
          id={id}
          onSelected={onServiceChangeWithSideEffects}
          renderSelectedValue={renderSelectedValue}
          selectedOption={selectedOption}
          serviceGroups={serviceGroups}
          shouldTruncateGroupHeader={hasLabelOverFlow}
        />
      </>
    );
  }

  const serviceGroupsUpdatedWithSelectedProperty = updateProviderServicesWithSelectedProperty(serviceGroups, selectedServiceItem);
  const selectedItemName = renderSelectedValue(selectedOption);

  return (
    <>
      <ShippingServiceSelector
        dropdownMenuShouldHaveWidthOfButton={dropdownMenuShouldHaveWidthOfButton}
        dropdownMenuType={dropdownMenuType}
        hasLabelOverFlow={hasLabelOverFlow}
        id={id}
        onSelected={onServiceChangeWithSideEffects}
        selectedItemName={selectedItemName}
        serviceGroups={serviceGroupsUpdatedWithSelectedProperty}
      />
    </>
  );
};
